<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script>
export default {
  name: 'App'
}
</script>

<style>
/* ===== CSS 变量（来自手机版原型风格）===== */
:root {
  /* 核心配色 */
  --primary-color: #1890ff;
  --success-color: #52c41a;
  --warning-color: #faad14;
  --danger-color: #ff4d4f;
  --info-color: #1890ff;

  /* 中性色 */
  --text-primary: #1a1a1a;
  --text-secondary: #666;
  --text-placeholder: #999;
  --border-base: #e8e8e8;
  --border-light: #f0f0f0;

  /* 背景色 */
  --bg-page: #f5f5f5;
  --bg-card: #ffffff;
  --bg-hover: #f5f5f5;
  --bg-active: rgba(24, 144, 255, 0.05);

  /* 状态色 */
  --status-online: #52c41a;
  --status-offline: #999;
  --status-warning: #faad14;
  --status-error: #ff4d4f;
  --status-charging: #1890ff;

  /* 间距 */
  --space-xs: 4px;
  --space-sm: 8px;
  --space-md: 12px;
  --space-lg: 16px;
  --space-xl: 20px;
  --space-xxl: 24px;

  /* 圆角 */
  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 14px;
  --radius-xl: 18px;

  /* 阴影 */
  --shadow-sm: 0 1px 4px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 2px 8px rgba(0, 0, 0, 0.08);

  /* 字体 */
  --font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  --font-size-xs: 12px;
  --font-size-sm: 14px;
  --font-size-md: 16px;
  --font-size-lg: 18px;
  --font-size-xl: 20px;
  --font-size-xxl: 24px;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
}

body {
  font-family: var(--font-family);
  background: var(--bg-page);
  color: var(--text-primary);
  line-height: 1.5;
  overflow: hidden;
}

/* ===== 通用工具类 ===== */
.uw-card {
  background: var(--bg-card);
  border-radius: var(--radius-md);
  padding: var(--space-lg);
  margin-bottom: var(--space-md);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
}

.uw-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-md);
}

.uw-card-title {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text-primary);
  position: relative;
  padding-left: 12px;
}

.uw-card-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 16px;
  background: var(--primary-color);
  border-radius: 2px;
}

/* 页面头部 */
.uw-page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-lg);
  padding: var(--space-lg) var(--space-xl);
  background: var(--bg-card);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
}

.uw-page-title {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--text-primary);
}

/* 标签 */
.uw-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  font-weight: 500;
  white-space: nowrap;
}

.uw-tag-success { background: #f6ffed; color: #52c41a; border: 1px solid #b7eb8f; }
.uw-tag-warning { background: #fffbe6; color: #faad14; border: 1px solid #ffe58f; }
.uw-tag-danger  { background: #fff1f0; color: #ff4d4f; border: 1px solid #ffccc7; }
.uw-tag-info    { background: #e6f7ff; color: #1890ff; border: 1px solid #91d5ff; }
.uw-tag-default { background: #fafafa; color: #666;     border: 1px solid #e8e8e8; }

/* 搜索栏 */
.uw-search-bar {
  background: var(--bg-card);
  border-radius: var(--radius-md);
  padding: var(--space-lg);
  margin-bottom: var(--space-md);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
}

/* 数据项网格 */
.uw-data-grid {
  display: grid;
  gap: var(--space-md);
}
.uw-data-grid-2 { grid-template-columns: repeat(2, 1fr); }
.uw-data-grid-3 { grid-template-columns: repeat(3, 1fr); }
.uw-data-grid-4 { grid-template-columns: repeat(4, 1fr); }

.uw-data-item {
  background: var(--bg-page);
  padding: var(--space-md);
  border-radius: var(--radius-sm);
}
.uw-data-label {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  margin-bottom: var(--space-xs);
}
.uw-data-value {
  font-size: var(--font-size-md);
  font-weight: 500;
  color: var(--text-primary);
}

/* 列表项 */
.uw-list-item {
  background: var(--bg-card);
  border-radius: var(--radius-md);
  padding: var(--space-lg);
  margin-bottom: var(--space-sm);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  transition: box-shadow 0.2s;
  cursor: pointer;
}
.uw-list-item:hover { box-shadow: var(--shadow-md); }
.uw-list-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-sm);
}
.uw-list-item-title {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text-primary);
}
.uw-list-item-info {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}
.uw-list-item-meta {
  display: flex;
  gap: var(--space-lg);
  flex-wrap: wrap;
  margin-top: var(--space-xs);
}

/* 统计卡片 */
.uw-stat-card {
  background: var(--bg-card);
  border-radius: var(--radius-md);
  padding: var(--space-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  display: flex;
  align-items: center;
  gap: var(--space-md);
}
.uw-stat-icon {
  width: 52px;
  height: 52px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  flex-shrink: 0;
}
.uw-stat-info .uw-stat-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 4px;
}
.uw-stat-info .uw-stat-label {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

/* 分页 */
.uw-pagination {
  margin-top: var(--space-lg);
  display: flex;
  justify-content: flex-end;
}

/* 详情分区标题 */
.uw-section-title {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text-primary);
  padding-left: 12px;
  border-left: 3px solid var(--primary-color);
  margin-bottom: var(--space-md);
}

/* 视频容器 */
.uw-video-box {
  position: relative;
  aspect-ratio: 16/9;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: var(--radius-md);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  margin-bottom: var(--space-md);
}
.uw-video-icon { font-size: 48px; opacity: 0.5; display: block; margin-bottom: 8px; }
.uw-video-text { font-size: var(--font-size-sm); opacity: 0.7; }

/* 审核时间轴 */
.uw-timeline { padding: 4px 0; }
.uw-timeline-step {
  display: flex;
  gap: var(--space-md);
  padding-bottom: var(--space-lg);
  position: relative;
}
.uw-timeline-step:last-child { padding-bottom: 0; }
.uw-timeline-step::before {
  content: '';
  position: absolute;
  left: 17px; top: 36px; bottom: 0;
  width: 2px;
  background: var(--border-base);
}
.uw-timeline-step:last-child::before { display: none; }
.uw-timeline-dot {
  width: 36px; height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  z-index: 1;
}
.uw-timeline-dot.done    { background: #e6f9ef; }
.uw-timeline-dot.pending { background: #fff8e6; }
.uw-timeline-dot.waiting { background: var(--bg-page); border: 2px dashed var(--border-base); }
.uw-timeline-body { flex: 1; padding-top: 6px; }
.uw-timeline-title { font-size: var(--font-size-sm); font-weight: 600; }
.uw-timeline-sub   { font-size: var(--font-size-xs); color: var(--text-secondary); margin-top: 2px; }

/* 控制按钮面板 */
.uw-control-panel {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-sm);
}
.uw-control-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-md) var(--space-sm);
  background: var(--bg-card);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-base);
  cursor: pointer;
  transition: all 0.2s;
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  text-align: center;
}
.uw-control-btn:hover {
  background: #e6f7ff;
  border-color: var(--primary-color);
  color: var(--primary-color);
}
.uw-control-btn .uw-ctrl-icon { font-size: 22px; margin-bottom: 4px; }
.uw-control-btn.danger:hover  { background: #fff1f0; border-color: var(--danger-color); color: var(--danger-color); }
.uw-control-btn.warning:hover { background: #fffbe6; border-color: var(--warning-color); color: var(--warning-color); }

/* 操作日志 */
.uw-log-item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  background: var(--bg-page);
  border-radius: var(--radius-sm);
  margin-bottom: var(--space-xs);
  font-size: var(--font-size-sm);
}
.uw-log-time { font-size: var(--font-size-xs); color: var(--text-secondary); white-space: nowrap; min-width: 46px; }
.uw-log-user { color: var(--primary-color); font-weight: 500; }

/* 检查项卡片 */
.uw-check-item {
  background: var(--bg-page);
  border-radius: var(--radius-sm);
  padding: var(--space-md);
  margin-bottom: var(--space-sm);
}
.uw-check-item-header {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-sm);
}
.uw-check-num {
  width: 24px; height: 24px;
  border-radius: 50%;
  background: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-sm);
  font-weight: 600;
  flex-shrink: 0;
}
.uw-check-result-row {
  display: flex;
  gap: var(--space-lg);
}
.uw-check-result-item { font-size: var(--font-size-sm); display: flex; align-items: center; gap: 4px; }

/* 表单美化 */
.uw-form .van-field__label {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
}

/* 文件上传框 */
.uw-upload-box {
  border: 2px dashed var(--border-base);
  border-radius: var(--radius-md);
  padding: var(--space-xl);
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--bg-page);
}
.uw-upload-box:hover {
  border-color: var(--primary-color);
  background: #e6f7ff;
}

/* 空状态 */
.uw-empty {
  text-align: center;
  padding: 48px 0;
  color: var(--text-secondary);
}
.uw-empty-icon { font-size: 48px; display: block; margin-bottom: 12px; opacity: 0.4; }
.uw-empty-text { font-size: var(--font-size-sm); }

/* 返回按钮 */
.uw-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  cursor: pointer;
  transition: color 0.2s;
  margin-bottom: var(--space-md);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
}
.uw-back-btn:hover { color: var(--primary-color); background: #e6f7ff; }

/* 告警卡片 */
.uw-alert-card {
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-md);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-sm);
  border: 1px solid #ffe58f;
  background: #fffbe6;
}
.uw-alert-icon { font-size: 20px; }
.uw-alert-title { font-size: var(--font-size-sm); font-weight: 600; margin-bottom: 2px; }
.uw-alert-sub   { font-size: var(--font-size-xs); color: var(--text-secondary); }

/* ===== 按钮样式（来自原型）===== */
.uw-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-xs);
  padding: 10px 16px;
  border-radius: var(--radius-md);
  border: none;
  font-size: var(--font-size-md);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--primary-color);
  color: white;
}
.uw-btn:hover { opacity: 0.9; }
.uw-btn:active { transform: scale(0.98); }
.uw-btn-block { width: 100%; }
.uw-btn-sm { padding: 6px 12px; font-size: var(--font-size-sm); }
.uw-btn-outline {
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
}
.uw-btn-outline:hover { background: rgba(24, 144, 255, 0.05); }
.uw-btn-success { background: var(--success-color); }
.uw-btn-warning { background: var(--warning-color); }
.uw-btn-danger  { background: var(--danger-color); }
.uw-btn-info    { background: var(--info-color); }

/* ===== 原型关键样式 ===== */
.main-wrapper {
  padding: var(--space-md);
}

/* 顶部导航 */
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--bg-card);
  border-bottom: 1px solid var(--border-base);
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-sm);
}

.header-title {
  font-size: var(--font-size-lg);
  font-weight: 600;
}

/* 底部导航 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--bg-card);
  border-top: 1px solid var(--border-base);
  display: flex;
  justify-content: space-around;
  padding: 8px 0;
  box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.05);
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
  padding: 4px;
  transition: color 0.2s;
}

.nav-item.active {
  color: var(--primary-color);
}

.nav-icon {
  font-size: 24px;
  margin-bottom: 2px;
}

.nav-label {
  font-size: var(--font-size-xs);
}

/* 卡片（原型样式） */
.card {
  background: var(--bg-card);
  border-radius: var(--radius-md);
  padding: var(--space-lg);
  margin-bottom: var(--space-md);
  box-shadow: var(--shadow-sm);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-md);
}

.card-title {
  font-size: var(--font-size-md);
  font-weight: 600;
}

/* 列表项（原型样式） */
.list-item {
  background: var(--bg-card);
  border-radius: var(--radius-md);
  padding: var(--space-lg);
  margin-bottom: var(--space-sm);
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s, box-shadow 0.2s;
}

.list-item:active {
  transform: scale(0.98);
}

.list-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-sm);
}

.list-item-title {
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--text-primary);
}

.list-item-id {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  margin-bottom: var(--space-xs);
}

.list-item-info {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin-right: var(--space-lg);
}

.list-item-info-row {
  display: flex;
  gap: var(--space-lg);
  flex-wrap: wrap;
  margin-top: var(--space-xs);
}

/* 标签（原型样式） */
.tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-xs);
  font-weight: 500;
  white-space: nowrap;
}

.tag.success {
  background: #f6ffed;
  color: var(--success-color);
  border: 1px solid #b7eb8f;
}

.tag.warning {
  background: #fffbe6;
  color: var(--warning-color);
  border: 1px solid #ffe58f;
}

.tag.danger {
  background: #fff1f0;
  color: var(--danger-color);
  border: 1px solid #ffccc7;
}

.tag.info {
  background: #e6f7ff;
  color: var(--info-color);
  border: 1px solid #91d5ff;
}

.tag.default {
  background: #fafafa;
  color: var(--text-secondary);
  border: 1px solid var(--border-base);
}

/* 数据网格 */
.data-grid {
  display: grid;
  gap: var(--space-md);
}

.data-grid-2 {
  grid-template-columns: repeat(2, 1fr);
}

.data-item {
  background: var(--bg-page);
  padding: var(--space-md);
  border-radius: var(--radius-sm);
}

.data-label {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  margin-bottom: var(--space-xs);
}

.data-value {
  font-size: var(--font-size-md);
  font-weight: 500;
}

/* 详情分区 */
.detail-section {
  margin-bottom: var(--space-xl);
}

.detail-section-title {
  font-size: var(--font-size-md);
  font-weight: 600;
  margin-bottom: var(--space-md);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.detail-section-title::before {
  content: "";
  width: 3px;
  height: 16px;
  background: var(--primary-color);
  border-radius: 2px;
}

/* 控制面板 */
.control-panel {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-sm);
}

.control-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-md) var(--space-sm);
  background: var(--bg-card);
  border: 1px solid var(--border-base);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s;
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  text-align: center;
}

.control-btn:active {
  transform: scale(0.95);
  background: var(--bg-active);
}

.control-btn-icon {
  font-size: 24px;
  margin-bottom: 4px;
}

.control-btn.danger {
  color: var(--danger-color);
}

.control-btn.warning {
  color: var(--warning-color);
}

.control-btn.success {
  color: var(--success-color);
}

/* ===== Vant 全局样式覆盖（更圆润的风格）===== */
.van-card { border-radius: var(--radius-md); }
.van-tag { border-radius: var(--radius-sm); }
.van-button--primary { border-radius: var(--radius-md); }
.van-button { border-radius: var(--radius-md); }
.van-cell-group { border-radius: var(--radius-md); overflow: hidden; }

html,
body,
#app {
  height: 100%;
}

.mobile-layout .main-content > * {
  min-height: 100% !important;
  box-sizing: border-box;
}
</style>
